/**
 * Protected Route components using Clerk auth
 */

import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthContext } from './ClerkAuthProvider';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  children: ReactNode;
  requireTenant?: boolean;
}

/**
 * Loading spinner component for auth states
 */
function AuthLoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <Loader2 className="h-8 w-8 animate-spin text-slate-600 mx-auto mb-4" />
        <p className="text-sm text-slate-500">Loading...</p>
      </div>
    </div>
  );
}

/**
 * Protected route component
 *
 * Redirects to sign-in if not authenticated.
 * Shows loading state while auth is initializing.
 * Optionally requires tenant membership.
 */
export function ProtectedRoute({
  children,
  requireTenant = true,
}: ProtectedRouteProps) {
  const location = useLocation();
  const { isLoading, isAuthenticated, tenantId } = useAuthContext();

  // Show loading while auth is initializing
  if (isLoading) {
    return <AuthLoadingScreen />;
  }

  // Redirect to sign-in if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/sign-in" state={{ from: location }} replace />;
  }

  // Check tenant requirement - redirect to onboarding if no tenant
  if (requireTenant && !tenantId) {
    return <Navigate to="/onboarding" replace />;
  }

  // Render protected content
  return <>{children}</>;
}

/**
 * Public route that redirects to dashboard if already authenticated
 */
export function PublicRoute({ children }: { children: ReactNode }) {
  const { isLoading, isAuthenticated, tenantId } = useAuthContext();

  // Show loading while auth is initializing
  if (isLoading) {
    return <AuthLoadingScreen />;
  }

  // Redirect to appropriate page if already authenticated
  if (isAuthenticated) {
    if (tenantId) {
      return <Navigate to="/dashboard" replace />;
    } else {
      return <Navigate to="/onboarding" replace />;
    }
  }

  return <>{children}</>;
}
